import { useCallback, useMemo, type ChangeEvent } from 'react'
import { Button, Input, InputNumber, Select, Switch, Typography } from 'antd'
import { EMPTY_VALUES, type FieldEdit, type FieldKind, type StructuredField } from '../config/groups'
import { VALUE_TYPES, valueChoices } from '../config/labels'

type Props = { field: StructuredField; disabled: boolean; onEdit: (edit: FieldEdit) => void; onAdvanced?: () => void }

/** Value controls never edit keys; deeper values are summaries, not another navigation hierarchy. */
export const StructuredFields = ({ field, disabled, onEdit, onAdvanced }: Props) => {
  const text = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => onEdit({ field, value: event.target.value }), [field, onEdit])
  const choose = useCallback((value: string) => onEdit({ field, value }), [field, onEdit])
  const choices = useMemo(() => field.kind === 'string' ? valueChoices(field.path, field.value as string) : undefined, [field])
  const number = useCallback((value: number | null) => {
    if (value !== null && Number.isFinite(value)) onEdit({ field, value })
  }, [field, onEdit])
  const toggle = useCallback((value: boolean) => onEdit({ field, value }), [field, onEdit])
  const type = useCallback((kind: FieldKind) => {
    if (kind !== field.kind) onEdit({ field, value: EMPTY_VALUES[kind]() })
  }, [field, onEdit])
  const rows = useMemo(() => ({ minRows: 1, maxRows: 5 }), [])
  const summary = <SpaceSummary field={field} onAdvanced={onAdvanced} disabled={disabled} />
  const controls = {
    string: choices ? <Select className="instance-administration__choice-value" aria-label={field.label} value={field.value as string} options={choices} onChange={choose} disabled={disabled} />
      : <Input.TextArea autoSize={rows} aria-label={field.label} value={field.value as string} onChange={text} disabled={disabled} />,
    number: <InputNumber aria-label={field.label} value={field.value as number} onChange={number} disabled={disabled} />,
    boolean: <Switch aria-label={field.label} checked={field.value === true} onChange={toggle} disabled={disabled} checkedChildren="开启" unCheckedChildren="关闭" />,
    null: <Typography.Text type="secondary">空值</Typography.Text>,
    object: summary, array: summary,
  }
  const nested = field.kind === 'object' || field.kind === 'array'
  return <div className="instance-administration__structured-value">
    {controls[field.kind]}
    {!nested ? <Select aria-label={`${field.label}的数据类型`} options={VALUE_TYPES} value={field.kind}
      onChange={type} disabled={disabled} className="instance-administration__value-type" /> : null}
  </div>
}

const SpaceSummary = ({ field, onAdvanced, disabled }: Pick<Props, 'field' | 'onAdvanced' | 'disabled'>) => <div className="instance-administration__value-summary">
  <Typography.Text type="secondary">{Object.keys(field.value ?? {}).length} 项</Typography.Text>
  {onAdvanced ? <Button type="link" size="small" onClick={onAdvanced} disabled={disabled} aria-label={'源码编辑' + field.label}>源码编辑</Button> : null}
</div>
